<template>
  <div class="card">
    <div class="card-header d-flex align-items-center justify-content-between">
      <div>
        <h4 class="card-title mb-0">
          Страница старта: гид, программа, FAQ, обновления
        </h4>
        <div class="text-muted small mt-25">
          Сохраняется отдельно от остальной формы старта. Пустые разделы на
          сайте не показываются.
        </div>
      </div>
      <b-button
        variant="primary"
        :disabled="loading || saving"
        @click="save"
      >
        <b-spinner
          v-if="saving"
          small
          class="mr-50"
        />
        Сохранить страницу
      </b-button>
    </div>

    <div class="card-body">
      <b-alert
        v-if="errorText"
        show
        variant="danger"
        class="mb-1"
      >
        {{ errorText }}
      </b-alert>

      <b-form-group
        label="Меддопуск — одна короткая строка для карточки под шапкой"
      >
        <b-form-input
          v-model="info.medical_note"
          maxlength="255"
          placeholder="Например: Справка нужна с 5 км"
        />
      </b-form-group>

      <!-- Гид участника -->
      <h5 class="mt-2">
        Гид участника
      </h5>
      <div
        v-for="(chapter, index) in info.guide"
        :key="'guide' + index"
        class="start-info__block"
      >
        <div class="start-info__row">
          <b-form-input
            v-model="chapter.title"
            maxlength="200"
            placeholder="Заголовок: как добраться, меддопуск, выдача пакетов…"
          />
          <b-button
            variant="outline-danger"
            size="sm"
            @click="info.guide.splice(index, 1)"
          >
            Удалить
          </b-button>
        </div>
        <quill-editor
          v-model="chapter.html"
          :options="editorOptions"
        />
      </div>
      <b-button
        variant="outline-primary"
        size="sm"
        @click="info.guide.push({ title: '', html: '' })"
      >
        Добавить главу гида
      </b-button>

      <!-- Программа -->
      <h5 class="mt-2">
        Программа
      </h5>
      <div
        v-for="(day, dayIndex) in info.program"
        :key="'day' + dayIndex"
        class="start-info__block"
      >
        <div class="start-info__row">
          <b-form-input
            v-model="day.date"
            type="date"
            class="start-info__date"
          />
          <b-button
            variant="outline-danger"
            size="sm"
            @click="info.program.splice(dayIndex, 1)"
          >
            Удалить день
          </b-button>
        </div>
        <div
          v-for="(item, itemIndex) in day.items"
          :key="'item' + itemIndex"
          class="start-info__row"
        >
          <b-form-input
            v-model="item.time"
            maxlength="32"
            class="start-info__time"
            placeholder="09:30–11:40"
          />
          <b-form-input
            v-model="item.title"
            maxlength="255"
            placeholder="Что происходит"
          />
          <b-form-input
            v-model="item.place"
            maxlength="255"
            placeholder="Где (необязательно)"
          />
          <b-button
            variant="outline-danger"
            size="sm"
            @click="day.items.splice(itemIndex, 1)"
          >
            ×
          </b-button>
        </div>
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="day.items.push({ time: '', title: '', place: '' })"
        >
          Добавить пункт
        </b-button>
      </div>
      <b-button
        variant="outline-primary"
        size="sm"
        @click="info.program.push({ date: '', items: [{ time: '', title: '', place: '' }] })"
      >
        Добавить день
      </b-button>

      <!-- FAQ -->
      <h5 class="mt-2">
        FAQ
      </h5>
      <div
        v-for="(item, index) in info.faq"
        :key="'faq' + index"
        class="start-info__block"
      >
        <div class="start-info__row">
          <b-form-input
            v-model="item.question"
            maxlength="500"
            placeholder="Вопрос"
          />
          <b-button
            variant="outline-danger"
            size="sm"
            @click="info.faq.splice(index, 1)"
          >
            Удалить
          </b-button>
        </div>
        <b-form-textarea
          v-model="item.answer"
          rows="2"
          max-rows="8"
          maxlength="5000"
          placeholder="Короткий ответ"
        />
      </div>
      <b-button
        variant="outline-primary"
        size="sm"
        @click="info.faq.push({ question: '', answer: '' })"
      >
        Добавить вопрос
      </b-button>

      <!-- Обновления -->
      <h5 class="mt-2">
        Журнал обновлений
      </h5>
      <div
        v-for="(item, index) in info.updates"
        :key="'update' + index"
        class="start-info__row"
      >
        <b-form-input
          v-model="item.date"
          type="date"
          class="start-info__date"
        />
        <b-form-input
          v-model="item.text"
          maxlength="1000"
          placeholder="Что изменилось"
        />
        <b-form-input
          v-model="item.section"
          maxlength="100"
          class="start-info__section"
          placeholder="Раздел"
        />
        <b-button
          variant="outline-danger"
          size="sm"
          @click="info.updates.splice(index, 1)"
        >
          ×
        </b-button>
      </div>
      <b-button
        variant="outline-primary"
        size="sm"
        @click="info.updates.unshift({ date: today(), text: '', section: '' })"
      >
        Добавить запись
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BSpinner,
} from 'bootstrap-vue'
import { quillEditor } from 'vue-quill-editor'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const emptyInfo = () => ({
  medical_note: '',
  guide: [],
  program: [],
  faq: [],
  updates: [],
})

/**
 * The start page's own content on the site (GET/PUT start/:id/info): the participant's guide, the programme by
 * days, the FAQ, the journal of changes and the line about the medical admission. Saved with its own button,
 * apart from the start form.
 */
export default {
  name: 'StartInfoSection',
  components: {
    BAlert,
    BButton,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BSpinner,
    quillEditor,
  },
  props: {
    startId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      errorText: '',
      info: emptyInfo(),
      editorOptions: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean'],
          ],
        },
      },
    }
  },
  watch: {
    startId: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },
  methods: {
    today() {
      return new Date().toISOString().slice(0, 10)
    },
    apply(data) {
      this.info = {
        medical_note: data.medical_note || '',
        guide: data.guide || [],
        program: (data.program || []).map(day => ({
          date: day.date,
          items: (day.items || []).map(item => ({
            ...item,
            place: item.place || '',
          })),
        })),
        faq: data.faq || [],
        updates: (data.updates || []).map(item => ({
          ...item,
          section: item.section || '',
        })),
      }
    },
    async load() {
      this.loading = true
      this.errorText = ''
      try {
        const { data } = await this.$http.get(`start/${this.startId}/info`)
        this.apply(data)
      } catch (error) {
        this.errorText = 'Не удалось загрузить содержимое страницы старта'
      } finally {
        this.loading = false
      }
    },
    /** Rows left entirely blank are dropped; a half-filled one is sent, and the API says what is missing */
    payload() {
      const filled = (...values) => values.some(value => String(value || '').trim() !== '')

      return {
        medical_note: this.info.medical_note,
        guide: this.info.guide.filter(item => filled(item.title, item.html)),
        program: this.info.program
          .map(day => ({
            date: day.date,
            items: day.items.filter(item => filled(item.time, item.title, item.place)),
          }))
          .filter(day => filled(day.date) || day.items.length > 0),
        faq: this.info.faq.filter(item => filled(item.question, item.answer)),
        updates: this.info.updates.filter(item => filled(item.text, item.section)),
      }
    },
    async save() {
      this.saving = true
      this.errorText = ''
      try {
        const { data } = await this.$http.put(
          `start/${this.startId}/info`,
          this.payload(),
        )
        this.apply(data)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Страница старта сохранена',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
        this.errorText = `Не удалось сохранить: ${
          Array.isArray(message) ? message.join('; ') : message || 'проверьте заполнение полей'
        }`
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.start-info {
  &__block {
    margin-bottom: 12px;
    padding: 12px;
    border: 1px solid #ebe9f1;
    border-radius: 6px;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__date {
    flex: 0 0 170px;
  }

  &__time {
    flex: 0 0 140px;
  }

  &__section {
    flex: 0 0 200px;
  }
}
</style>
